/* RemoveInvalidDevicesRenderer.js */

"use strict";
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const InputCheckbox = require("@gluenetworks/steve_ui_core").Control.Boolean.InputCheckbox;
const _ = require("lodash");

class RemoveInvalidDevicesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("remove-screen-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Remove Invalid Devices";

        this._removeInputCheckbox = new InputCheckbox({
            label: "Remove devices that have failed the validation process from the current OS Plan",
            labelShown: true,
            value: false,
            darkTheme: true,
        });

        this._createNewOsPlanContainer = document.createElement("div");
        this._createNewOsPlanContainer.classList.add("create-new-os-plan-container");
        this._createInputCheckbox = new InputCheckbox({
            label: "Move invalid devices to a new OS Plan named:",
            labelShown: true,
            value: false,
            darkTheme: true,
            disabled: true,
        });
        this._newOsPlanNameInput = new InputTextControl({
            darkTheme: true,
            placeholder: "Enter a new OS Plan name",
            value: "",
            disabled: true,
        });

        this._footerContainer = document.createElement("div");
        this._footerContainer.classList.add("footer-container");
        this._invalidDeviceCountText = document.createElement("div");
        this._invalidDeviceCountText.classList.add("invalid-device-count-text");
        this._invalidDeviceCountText.innerHTML = "";
        this._buttonBarElem = document.createElement("button-container");
        this._buttonBarElem.classList.add("button-bar");
        this._okButton = new ButtonControl({
            label: "OK",
            active: true,
            disabled: true,
        });
        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._removeInputCheckbox.on("Value", this._onRemoveInputCheckboxUpdate.bind(this));
        this._createInputCheckbox.on("Value", this._onCreateInputCheckboxUpdate.bind(this));
        this._newOsPlanNameInput.on("Value", this._onNewOsPlanNameInputUpdate.bind(this));
        this._okButton.on("UserClick", this._onOkButtonClick.bind(this));
        this._cancelButton.on("UserClick", this._onCancelButtonClick.bind(this));
    }

    _unregisterListeners() {
        this._removeInputCheckbox.removeAllListeners("Value");
        this._createInputCheckbox.removeAllListeners("Value");
        this._newOsPlanNameInput.removeAllListeners("Value");
        this._okButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let promArr = [
            this._newOsPlanNameInput.shutdown,
            this._removeInputCheckbox.shutdown,
            this._createInputCheckbox.shutdown,
            this._okButton.shutdown,
            this._cancelButton.shutdown,
        ];

        return Promise.all(promArr).then(
            () => {
                this._titleText.remove();
                this._titleText = undefined;
                this._removeInputCheckbox = undefined;
                this._createNewOsPlanContainer.remove();
                this._createNewOsPlanContainer = undefined;
                this._newOsPlanNameInput = undefined;
                this._createInputCheckbox = undefined;
                this._buttonBarElem.remove();
                this._okButton = undefined;
                this._cancelButton = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "RemoveInvalidDevicesRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        const count = this._model._osPlanDeviceUnhealthyCount;

        return this._model
            ._generateOsPlanName(this._model._osPlanEntity.name)
            .then((newOsPlanName) => {
                this._newOsPlanNameInput.value = newOsPlanName || "";
                if (!this._rootElem.hasChildNodes()) {
                    this._rootElem.appendChild(this._titleText);
                    this._rootElem.appendChild(this._removeInputCheckbox.renderer.rootElement);
                    this._rootElem.appendChild(this._createNewOsPlanContainer);
                    this._createNewOsPlanContainer.appendChild(
                        this._createInputCheckbox.renderer.rootElement
                    );
                    this._createNewOsPlanContainer.appendChild(
                        this._newOsPlanNameInput.renderer.rootElement
                    );
                    if (count > 0) {
                        this._invalidDeviceCountText.innerHTML = "Total failed devices: " + count;
                        this._rootElem.appendChild(this._invalidDeviceCountText);
                    }
                    this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                    this._buttonBarElem.appendChild(this._okButton.renderer.rootElement);

                    this._footerContainer.appendChild(this._invalidDeviceCountText);
                    this._footerContainer.appendChild(this._buttonBarElem);

                    this._rootElem.appendChild(this._footerContainer);
                }
                return Promise.resolve();
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onRemoveInputCheckboxUpdate() {
        this._createInputCheckbox.disabled = !this._removeInputCheckbox.value;
        this._enableDisableOkButtonClick();
    }

    _onCreateInputCheckboxUpdate() {
        this._newOsPlanNameInput.disabled = !this._createInputCheckbox.value;
        this._enableDisableOkButtonClick();
    }

    _onOkButtonClick() {
        this._model.removeInvalidDevices({
            deleteFromPlan: _.get(this._removeInputCheckbox, "value", true),
            moveToAnotherPlan: _.get(this._createInputCheckbox, "value", false),
            planName: _.get(this._newOsPlanNameInput, "value", "").trim(),
        });
    }

    _onNewOsPlanNameInputUpdate() {
        this._enableDisableOkButtonClick();
    }

    _onCancelButtonClick() {
        this._model.cancelAction();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _enableDisableOkButtonClick() {
        if (this._removeInputCheckbox.value) {
            if (this._createInputCheckbox.value) {
                this._okButton.disabled = !this._newOsPlanNameInput.value.trim();
            } else {
                this._okButton.disabled = false;
            }
        } else {
            this._okButton.disabled = true;
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = RemoveInvalidDevicesRenderer;
